import { YES_NO } from "../assessment-constant";
import { FreeTextQuestionDef, FreeTextScreenerDef } from "../assessment-types";

const DIABETIC_GROUPS = {
  eyes: "Eyes",
  feet: "Feet",
  mouth: "Mouth",
  kidney: "Kidney Heart and Vascular",
} as const;

type Group = (typeof DIABETIC_GROUPS)[keyof typeof DIABETIC_GROUPS];

const DIABETIC_PLACEHOLDERS = {
  glucose: "e.g., 120 mg/dL",
  diet: "e.g., Diabetic diet, low carb",
  visualAcuity: "e.g., 20/20",
  intraocularPressure: "e.g., 15 mmHg",
  describeFindings: "Describe findings",
  pedalPulse: "e.g., +2 bilaterally",
  caries: "Present / absent / describe",
  specify: "Specify",
  specialist: "Specify specialist",
  riskFactors: "e.g., HTN, hyperlipidemia",
  bloodPressure: "e.g., 130/80 goal, 140/85 measured",
  triglycerides: "e.g., LDL < 100 goal, 130 measured mg/dL",
  urineAlbumin: "e.g., 30 mg/g",
  serumCreatinine: "e.g., 1.0 mg/dL / 90 mL/min",
  potassium: "e.g., 4.0 mEq/L",
  cardiacHistory: "Describe or write None",
  kidneyTests: "Specify tests and results",
} as const;

const text = (
  questionId: number,
  questionText: string,
  group: Group | undefined,
  placeholder: string
): FreeTextQuestionDef => ({
  questionId,
  questionText,
  group,
  kind: "text",
  audience: "PATIENT",
  placeholder,
});

const yesNo = (
  questionId: number,
  questionText: string,
  group?: Group
): FreeTextQuestionDef => ({
  questionId,
  questionText,
  group,
  kind: "yesNo",
  audience: "PATIENT",
  options: YES_NO,
});

// Diabetic Screening (DIABETES_ASSESSMENT), matched to the API's questions by
// their text; questions the API adds beyond this list render from its answer options.
export const DIABETIC_SCREENER: FreeTextScreenerDef = {
  hiddenQuestions: ["Patient signature", "Provider signature"],
  questions: [
    {
      questionId: 1,
      questionText: "Diabetes",
      group: undefined,
      kind: "single",
      audience: "PATIENT",
      options: ["Type 1", "Type 2", "Gestational", "Pre-Diabetes", "Unknown"],
    },
    {
      questionId: 2,
      questionText: "Current diabetes therapy",
      group: undefined,
      kind: "multi",
      audience: "PATIENT",
      options: ["Insulin", "Oral hypoglycemic", "Diet Control", "None"],
      exclusiveOption: "None",
    },
    text(
      3,
      "Results of last finger-stick blood glucose reading (per patient)",
      undefined,
      DIABETIC_PLACEHOLDERS.glucose
    ),
    yesNo(4, "Dietary counseling", undefined),
    text(5, "Type of diet", undefined, DIABETIC_PLACEHOLDERS.diet),
    yesNo(6, "Patient reports under control", undefined),
    text(
      7,
      "Visual acuity (best corrected): Right / Left",
      DIABETIC_GROUPS.eyes,
      DIABETIC_PLACEHOLDERS.visualAcuity
    ),
    text(
      9,
      "Intraocular pressure: Right / Left",
      DIABETIC_GROUPS.eyes,
      DIABETIC_PLACEHOLDERS.intraocularPressure
    ),
    yesNo(11, "No diabetic retinopathy", DIABETIC_GROUPS.eyes),
    yesNo(12, "Non-proliferative diabetic retinopathy", DIABETIC_GROUPS.eyes),
    yesNo(13, "Proliferative diabetic retinopathy", DIABETIC_GROUPS.eyes),
    yesNo(14, "Clinically significant macular edema", DIABETIC_GROUPS.eyes),
    text(
      15,
      "Note musculoskeletal deformities",
      DIABETIC_GROUPS.feet,
      DIABETIC_PLACEHOLDERS.describeFindings
    ),
    text(
      16,
      "Pedal pulse",
      DIABETIC_GROUPS.feet,
      DIABETIC_PLACEHOLDERS.pedalPulse
    ),
    {
      questionId: 17,
      questionText: "Risk categorization",
      group: DIABETIC_GROUPS.feet,
      kind: "single",
      audience: "PATIENT",
      options: ["Low risk patient", "High risk patient"],
    },
    text(18, "Caries", DIABETIC_GROUPS.mouth, DIABETIC_PLACEHOLDERS.caries),
    text(
      19,
      "Periodontal (health, abscesses, gingivitis, periodontitis)",
      DIABETIC_GROUPS.mouth,
      DIABETIC_PLACEHOLDERS.describeFindings
    ),
    text(
      20,
      "Functional (eating, swallowing etc) concerns",
      DIABETIC_GROUPS.mouth,
      DIABETIC_PLACEHOLDERS.describeFindings
    ),
    text(
      21,
      "Additional testing/treatment recommended",
      DIABETIC_GROUPS.mouth,
      DIABETIC_PLACEHOLDERS.specify
    ),
    text(
      22,
      "Refer to specialist",
      DIABETIC_GROUPS.mouth,
      DIABETIC_PLACEHOLDERS.specialist
    ),
    text(
      23,
      "Risk factors in addition to diabetes",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.riskFactors
    ),
    text(
      24,
      "Blood pressure (goal / measured)",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.bloodPressure
    ),
    text(
      26,
      "Triglycerides (LDL goal and measured values for all)",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.triglycerides
    ),
    {
      questionId: 30,
      questionText: "Smoking status",
      group: DIABETIC_GROUPS.kidney,
      kind: "single",
      audience: "PATIENT",
      options: ["Never", "Current", "Former"],
    },
    text(
      31,
      "Urine albumin to creatinine ratio",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.urineAlbumin
    ),
    text(
      32,
      "Serum creatinine and estimated GFR",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.serumCreatinine
    ),
    text(
      34,
      "Potassium",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.potassium
    ),
    text(
      35,
      "History of myocardial infarction, heart failure, or stroke",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.cardiacHistory
    ),
    text(
      36,
      "Kidney tests (ultrasound, CT scan, angiogram)",
      DIABETIC_GROUPS.kidney,
      DIABETIC_PLACEHOLDERS.kidneyTests
    ),
  ],
};
